import { ethers } from 'ethers';
import { getAllRpcUrls } from '../../config/rpcConfig';

// Function to test RPC endpoint health
const testRpcEndpoint = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_blockNumber',
        params: [],
        id: 1,
      }),
    });
    
    if (!response.ok) return false;
    
    const data = await response.json();
    return !data.error && data.result;
  } catch (error) {
    console.warn(`RPC endpoint ${url} failed:`, error);
    return false;
  }
};

// Function to get working RPC endpoint
export const getWorkingRpcEndpoint = async (chainId: number): Promise<string> => {
  const endpoints = getAllRpcUrls(chainId);
  
  if (endpoints.length === 0) {
    throw new Error(`Unsupported chain ID: ${chainId}`);
  }
  
  // Test endpoints in parallel
  const testPromises = endpoints.map(async (endpoint) => {
    const isWorking = await testRpcEndpoint(endpoint);
    return { endpoint, isWorking };
  });
  
  const results = await Promise.all(testPromises);
  const workingEndpoint = results.find(result => result.isWorking);
  
  if (!workingEndpoint) {
    throw new Error(`No working RPC endpoint found for chain ID ${chainId}`);
  }
  
  console.log(`Using RPC endpoint: ${workingEndpoint.endpoint} for chain ${chainId}`);
  return workingEndpoint.endpoint;
};

// Function to create provider with fallback
export const createProviderWithFallback = async (
  chainId: number,
  walletProvider?: any
): Promise<ethers.providers.Provider> => {
  try {
    // If wallet provider is available, use it
    if (walletProvider) {
      return new ethers.providers.Web3Provider(walletProvider);
    }
    
    // Otherwise, create a provider with working RPC endpoint
    const rpcUrl = await getWorkingRpcEndpoint(chainId);
    return new ethers.providers.JsonRpcProvider(rpcUrl);
  } catch (error) {
    console.error('Failed to create provider:', error);
    throw new Error(`Failed to connect to blockchain network for chain ID ${chainId}`);
  }
};

// Function to handle RPC errors with retry logic
export const executeWithRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      
      // Check if it's a rate limit error
      if (error.code === 429 || error.message?.includes('Too Many Requests')) {
        const waitTime = delay * Math.pow(2, attempt - 1); // Exponential backoff
        console.warn(`Rate limited, waiting ${waitTime}ms before retry ${attempt}/${maxRetries}`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }
      
      // Check if it's a server error
      if (error.code === -32603 || error.message?.includes('Internal error')) {
        console.warn(`Server error, retrying ${attempt}/${maxRetries}`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // For other errors, don't retry
      throw error;
    }
  }
  
  throw lastError!;
};

// Function to get balance with retry logic
export const getBalanceWithRetry = async (
  provider: ethers.providers.Provider,
  address: string,
  maxRetries: number = 3
): Promise<ethers.BigNumber> => {
  return executeWithRetry(
    () => provider.getBalance(address),
    maxRetries
  );
}; 